import { supabase, uploadContactPhoto } from './supabase';
import { dataUrlToFile, isPendingPhoto } from './contactPhoto';

/** How long the sign-up progress card keeps a "photos not saved" notice on screen. */
export const SIGNUP_PHOTO_NOTICE_MS = 5000;

/**
 * Sign-up, last step: upload the contact photos that were cropped earlier and
 * kept on the device (see isPendingPhoto), now that the account exists.
 *
 * - Uploads ONLY with a signed-in session. Without one, nothing is sent.
 * - A photo that cannot be uploaded is dropped from that contact, never saved
 *   as a data: URL, and never blocks the account or the payment. `failed`
 *   tells the caller to say so.
 * - Contacts without a pending photo are returned untouched.
 */
export async function uploadPendingContactPhotos<T extends { photoUrl?: string }>(
  contacts: T[],
  upload: (file: File) => Promise<string> = uploadContactPhoto,
): Promise<{ contacts: T[]; failed: number }> {
  const pendingCount = contacts.filter((c) => isPendingPhoto(c.photoUrl)).length;
  if (pendingCount === 0) return { contacts, failed: 0 };

  const dropPending = (c: T): T => (isPendingPhoto(c.photoUrl) ? { ...c, photoUrl: undefined } : c);

  let signedIn = false;
  try {
    const { data } = await supabase.auth.getSession();
    signedIn = !!data.session;
  } catch (err) {
    console.error('[ContactPhotos] Could not read the session:', err);
  }
  if (!signedIn) {
    console.error('[ContactPhotos] Not signed in — contact photos were not uploaded.');
    return { contacts: contacts.map(dropPending), failed: pendingCount };
  }

  let failed = 0;
  const result: T[] = [];
  for (const contact of contacts) {
    if (!isPendingPhoto(contact.photoUrl)) {
      result.push(contact);
      continue;
    }
    try {
      const url = await upload(dataUrlToFile(contact.photoUrl));
      result.push({ ...contact, photoUrl: url });
    } catch (err) {
      console.error('[ContactPhotos] Upload after sign-up failed:', err);
      failed += 1;
      result.push(dropPending(contact));
    }
  }
  return { contacts: result, failed };
}
